import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";

const LIST_FIELDS = [
  ["incident_types", "Incident categories"],
  ["document_types", "Document types"],
  ["addition_types", "Payroll addition types"],
  ["deduction_types", "Payroll deduction types"],
  ["report_types", "Report types"],
];

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "system_admin";
  const [branches, setBranches] = useState([]);
  const [demo, setDemo] = useState(null);
  const [mode, setMode] = useState(null);
  const [rates, setRates] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [config, setConfig] = useState(null);
  const [bForm, setBForm] = useState({ name: "", address: "", phone: "" });
  const [dForm, setDForm] = useState({ date: "", day_type: "holiday", name: "" });
  const [cfgText, setCfgText] = useState({});
  const [agencyName, setAgencyName] = useState("");

  const load = () => {
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
    api.get("/calendar").then((r) => setCalendar(r.data)).catch(() => {});
    if (isAdmin) api.get("/admin/demo-login").then((r) => setDemo(r.data)).catch(() => setDemo(null));
    if (isAdmin) api.get("/admin/demo-mode").then((r) => setMode(r.data)).catch(() => setMode(null));
    api.get("/payroll/statutory-tables").then((r) => setRates(r.data)).catch(() => setRates([]));
    api.get("/config").then((r) => {
      setConfig(r.data);
      setAgencyName(r.data.agency_name || "");
      const t = {};
      LIST_FIELDS.forEach(([k]) => { t[k] = (r.data[k] || []).join(", "); });
      setCfgText(t);
    }).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(load, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const addBranch = async () => {
    try {
      await api.post("/branches", bForm);
      toast.success("Branch added");
      setBForm({ name: "", address: "", phone: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addDay = async () => {
    try {
      await api.post("/calendar", dForm);
      toast.success("Calendar day set");
      setDForm({ date: "", day_type: "holiday", name: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const removeDay = async (date) => {
    await api.delete(`/calendar/${date}`).catch((e) => toast.error(errMsg(e)));
    load();
  };

  // Hiding the buttons DISABLES them: the same setting gates /auth/demo-accounts
  // and /auth/demo-login on the server, so a hidden button is not merely
  // unrendered -- the endpoint refuses. It can only ever take away: with
  // DEMO_QUICK_LOGIN off for the deployment, this switch cannot turn it on.
  const setDemoVisible = async (visible) => {
    try {
      const { data } = await api.put("/admin/demo-login", { visible });
      setDemo(data);
      await api.get("/admin/demo-mode").then((r) => setMode(r.data)).catch(() => {});
      toast.success(visible ? "One-click sign-in shown" : "One-click sign-in hidden and disabled");
    } catch (e) { toast.error(errMsg(e)); }
  };

  // Standard #6. HIDING IS NOT DELETING, and the copy below says so in those
  // words -- a client told "the demo data is gone" when it is merely filtered
  // will eventually watch it come back. The refusal path matters as much as the
  // switch: if a REAL record references a seeded one, the server answers 409
  // with the counts rather than leaving that record showing an owner the app
  // has stopped serving, and the operator has to say so explicitly.
  const setDataVisible = async (visible, force = false) => {
    try {
      const { data } = await api.put("/admin/demo-mode/data", { visible, force });
      setMode(data);
      await api.get("/admin/demo-login").then((r) => setDemo(r.data)).catch(() => {});
      toast.success(visible ? "Demo data shown again" : "Demo data hidden — the records are still there");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.counts) {
        const what = Object.entries(d.counts).map(([k, v]) => `${v} ${k}`).join(", ");
        if (window.confirm(
          `Real records reference the demo data: ${what}.

` +
          `Hiding it would leave those rows showing an owner the app refuses to display. ` +
          `Hide anyway?`
        )) return setDataVisible(visible, true);
        return;
      }
      toast.error(errMsg(e));
    }
  };

  // The review gate needs a door, or the UNVERIFIED banner on every payslip can
  // never clear and people learn to ignore it -- which is worse than not having
  // it. Signing off records WHO and WHEN; editing the figures afterwards clears
  // that signature, because it covered the numbers that were signed.
  const reviewRate = async (t) => {
    if (!window.confirm(
      `Confirm the ${t.name} against the current circular?\n\n` +
      `Effective from ${t.effective_from}\nSource on file: ${t.source}\n\n` +
      `This records your name against these figures and removes the "unverified rates" ` +
      `warning from payslips and remittance schedules. Only do this if you have actually checked.`
    )) return;
    try {
      const { data } = await api.post(`/payroll/statutory-tables/${t.id}/review`, {});
      setRates((rs) => rs.map((x) => (x.id === data.id ? data : x)));
      toast.success(`${data.name} marked reviewed`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveConfig = async () => {
    try {
      // ⚠️ EVERY NUMERIC KEY MUST BE NAMED HERE. `PUT /config` overwrites
      // `value` wholesale, so a key this page does not send back is wiped on the
      // next save and silently reverts to its default — with nobody told.
      const body = { ...(config || {}), agency_name: agencyName,
                     payroll_factor: parseInt(config?.payroll_factor ?? 313, 10) || 313,
                     late_grace_minutes: Math.max(0, parseInt(config?.late_grace_minutes ?? 15, 10) || 0) };
      LIST_FIELDS.forEach(([k]) => { body[k] = cfgText[k].split(",").map((s) => s.trim()).filter(Boolean); });
      delete body.approval_workflows;
      await api.put("/config", body);
      toast.success("Configuration saved");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader title="Settings" sub="Agency profile, branches, business calendar, configurable lists" />
      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <h2 className="font-heading text-lg font-bold mb-2">Branches</h2>
          <DataTable testid="branches-table" rows={branches}
            columns={[
              { key: "name", label: "Name", render: (r) => <span className="font-semibold">{r.name}</span> },
              { key: "address", label: "Address" },
              { key: "phone", label: "Phone", render: (r) => <span className="font-mono text-xs">{r.phone}</span> },
            ]} />
          <div className="grid grid-cols-3 gap-2 mt-2 items-end">
            <div><Label className="text-[10px] uppercase font-bold">Name</Label><Input data-testid="branch-name" className="rounded-none mt-1 h-8" value={bForm.name} onChange={(e) => setBForm({ ...bForm, name: e.target.value })} /></div>
            <div><Label className="text-[10px] uppercase font-bold">Address</Label><Input data-testid="branch-address" className="rounded-none mt-1 h-8" value={bForm.address} onChange={(e) => setBForm({ ...bForm, address: e.target.value })} /></div>
            <div className="flex gap-2 items-end">
              <div className="flex-1"><Label className="text-[10px] uppercase font-bold">Phone</Label><Input data-testid="branch-phone" className="rounded-none mt-1 h-8" value={bForm.phone} onChange={(e) => setBForm({ ...bForm, phone: e.target.value })} /></div>
              <Button size="sm" data-testid="branch-add" className="rounded-none" onClick={addBranch} disabled={!bForm.name}>Add</Button>
            </div>
          </div>

          <h2 className="font-heading text-lg font-bold mt-8 mb-2">Business calendar</h2>
          <DataTable testid="calendar-table" rows={calendar} empty="No special days configured (Sundays default to rest day)"
            columns={[
              { key: "date", label: "Date", render: (r) => <span className="font-mono text-xs">{r.date}</span> },
              { key: "day_type", label: "Type", render: (r) => <StatusBadge value={r.day_type} /> },
              { key: "name", label: "Name" },
              { key: "x", label: "", render: (r) => <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`cal-del-${r.date}`} onClick={() => removeDay(r.date)}>Remove</Button> },
            ]} />
          <div className="grid grid-cols-4 gap-2 mt-2 items-end">
            <div><Label className="text-[10px] uppercase font-bold">Date</Label><Input data-testid="cal-date" type="date" className="rounded-none mt-1 h-8" value={dForm.date} onChange={(e) => setDForm({ ...dForm, date: e.target.value })} /></div>
            <div>
              <Label className="text-[10px] uppercase font-bold">Type</Label>
              <Select value={dForm.day_type} onValueChange={(v) => setDForm({ ...dForm, day_type: v })}>
                <SelectTrigger data-testid="cal-type" className="rounded-none mt-1 h-8"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="regular">Regular</SelectItem><SelectItem value="rest">Rest</SelectItem><SelectItem value="special">Special</SelectItem><SelectItem value="holiday">Holiday</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-[10px] uppercase font-bold">Name</Label><Input data-testid="cal-name" className="rounded-none mt-1 h-8" value={dForm.name} onChange={(e) => setDForm({ ...dForm, name: e.target.value })} /></div>
            <Button size="sm" data-testid="cal-add" className="rounded-none" onClick={addDay} disabled={!dForm.date}>Set day</Button>
          </div>
        </div>

        <div>
          <h2 className="font-heading text-lg font-bold mb-2">Configuration</h2>
          <div className="mb-3">
            <Label className="text-[10px] uppercase font-bold">Agency name (PDF branding)</Label>
            <Input data-testid="cfg-agency-name" className="rounded-none mt-1" value={agencyName} onChange={(e) => setAgencyName(e.target.value)} />
          </div>
          {LIST_FIELDS.map(([k, label]) => (
            <div key={k} className="mb-3">
              <Label className="text-[10px] uppercase font-bold">{label} (comma-separated)</Label>
              <Textarea data-testid={`cfg-${k}`} className="rounded-none mt-1 font-mono text-xs" rows={2} value={cfgText[k] || ""} onChange={(e) => setCfgText({ ...cfgText, [k]: e.target.value })} />
            </div>
          ))}
          <div className="mb-3">
            <Label className="text-[10px] uppercase font-bold">Payroll factor (days per year)</Label>
            <Input data-testid="cfg-payroll-factor" type="number" min="1" max="366"
              className="rounded-none mt-1 font-mono text-xs w-32"
              value={config?.payroll_factor ?? 313}
              onChange={(e) => setConfig({ ...config, payroll_factor: parseInt(e.target.value || "0", 10) })} />
            <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
              Turns a daily rate into the monthly basic that SSS, PhilHealth and Pag-IBIG are indexed on
              (daily × factor ÷ 12). 313 is the common six-day-week factor; 314, 261 and 365 are also used.
              <span className="font-bold"> Changing it moves every guard's contribution bracket</span>, so
              re-run any open payroll afterwards.
            </p>
          </div>
          <div className="mb-3">
            <Label className="text-[10px] uppercase font-bold">Late grace period (minutes)</Label>
            <Input data-testid="cfg-late-grace" type="number" min="0" max="120"
              className="rounded-none mt-1 font-mono text-xs w-32"
              value={config?.late_grace_minutes ?? 15}
              onChange={(e) => setConfig({ ...config, late_grace_minutes: parseInt(e.target.value || "0", 10) })} />
            <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
              How far past the scheduled start a check-in still counts as on time, on the dashboard&apos;s
              <span className="font-bold"> Late today</span> tile. It used to be zero: every guard who
              arrived four minutes after their start time was flagged, so the tile read 7 late out of
              seven posted. <span className="font-bold">A count that fires on everybody is one nobody
              reads.</span> It does not change pay — payroll prices the hours actually worked either way.
            </p>
          </div>
          <Button data-testid="cfg-save" className="rounded-none" onClick={saveConfig}>Save configuration</Button>

          <div className="mt-8 border border-border p-4" data-testid="statutory-rates">
            <h2 className="font-heading text-lg font-bold leading-none">Statutory rates</h2>
            <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
              The schedules payroll uses for SSS, PhilHealth, Pag-IBIG and withholding tax. A run resolves
              the table that was <span className="font-bold">in force for the period it pays</span>, so
              reprinting an old payslip reproduces the figures it was paid on.
            </p>
            <p className="text-xs mt-2 leading-relaxed">
              <span className="font-bold">These were seeded from published schedules and are not an authority.</span>{" "}
              <span className="text-muted-foreground">
                Rates change by circular. Until an administrator confirms each one, every payslip and
                remittance file prints an unverified-rates warning.
              </span>
            </p>
            <div className="mt-3 border border-border">
              <div className="grid grid-cols-[1fr_auto_auto_auto] gap-2 px-3 py-2 border-b border-border font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                <span>Schedule</span><span>Effective</span><span>State</span><span></span>
              </div>
              {rates.length === 0 && <div className="px-3 py-2 font-mono text-[10px] text-muted-foreground">No schedules loaded.</div>}
              {rates.map((t) => (
                <div key={t.id} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 px-3 py-2 items-center border-b border-border last:border-b-0">
                  <div>
                    <div className="font-semibold text-xs">{t.name}</div>
                    <div className="font-mono text-[10px] text-muted-foreground">{t.source}</div>
                  </div>
                  <span className="font-mono text-[10px]">{t.effective_from}</span>
                  {t.reviewed_at ? (
                    <span className="font-mono text-[10px] uppercase tracking-wider text-[hsl(var(--success))]"
                      data-testid={`rate-reviewed-${t.kind}`}>
                      Reviewed<br />
                      <span className="text-muted-foreground normal-case tracking-normal">
                        {t.reviewed_by} · {String(t.reviewed_at).slice(0, 10)}
                      </span>
                    </span>
                  ) : (
                    <span className="font-mono text-[10px] uppercase tracking-wider text-[hsl(var(--alert))]"
                      data-testid={`rate-unreviewed-${t.kind}`}>Unverified</span>
                  )}
                  {isAdmin && !t.reviewed_at && (
                    <Button size="sm" variant="outline" data-testid={`rate-review-${t.kind}`}
                      className="rounded-none h-7 text-[10px] font-bold uppercase tracking-wider"
                      onClick={() => reviewRate(t)}>Confirm</Button>
                  )}
                  {(!isAdmin || t.reviewed_at) && <span />}
                </div>
              ))}
            </div>
          </div>

          {isAdmin && mode && (
            <div className="mt-8 border border-border p-4" data-testid="demo-data-settings">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="font-heading text-lg font-bold leading-none">Demonstration data</h2>
                  <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                    Hides every seeded client, site, guard, shift, incident, payroll line and invoice
                    from <span className="font-bold">every screen and every total</span> — one
                    server-side filter, so a list and the dashboard above it cannot disagree.
                    Hiding the data hides the one-click sign-in buttons too, because those sign in
                    as seeded accounts.
                  </p>
                  <p className="text-xs mt-2 leading-relaxed">
                    <span className="font-bold">Hiding is not deleting.</span>{" "}
                    <span className="text-muted-foreground">
                      The records stay in the database and this switch is reversible. Removing them
                      is a separate, deliberate act: run the seeder with <code className="font-mono">--purge</code>.
                    </span>
                  </p>
                </div>
                <Switch
                  data-testid="demo-data-toggle"
                  checked={!!mode.data_visible}
                  disabled={!mode.env_enabled}
                  onCheckedChange={(v) => setDataVisible(v)}
                />
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.15em] mt-3 text-muted-foreground">
                {mode.data_visible
                  ? `Showing ${Object.values(mode.hiding || {}).reduce((a, b) => a + b, 0)} seeded record${
                      Object.values(mode.hiding || {}).reduce((a, b) => a + b, 0) === 1 ? "" : "s"}`
                  : `Hiding ${Object.entries(mode.hiding || {}).map(([k, v]) => `${v} ${k}`).join(" · ") || "nothing"}`}
              </div>
              {mode.note && (
                <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">{mode.note}</p>
              )}
            </div>
          )}

          {isAdmin && demo && (
            <div className="mt-8 border border-border p-4" data-testid="demo-login-settings">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="font-heading text-lg font-bold leading-none">One-click sign-in</h2>
                  <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                    Shows one button per demonstration account on the login page. Hiding them also
                    <span className="font-bold"> disables the endpoint</span> — not just the buttons.
                    Password sign-in and your own administrator account are unaffected.
                  </p>
                </div>
                <Switch
                  data-testid="demo-login-toggle"
                  checked={!!demo.visible}
                  disabled={!demo.env_enabled}
                  onCheckedChange={setDemoVisible}
                />
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.15em] mt-3 text-muted-foreground">
                {demo.enabled
                  ? `Showing ${demo.account_count} demonstration account${demo.account_count === 1 ? "" : "s"}`
                  : `Not shown — ${demo.state}`}
              </div>
              {(demo.note || demo.reason) && (
                <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">{demo.note || demo.reason}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
